package dev.lingvo.site.components.studio.preview

import dev.lingvo.shared.screenshots.render.RenderReport
import kotlinx.browser.window

/** Includes input provenance so a new capture or caption cannot reuse stale pixels. */
data class ThumbnailKey(val documentSha: String, val slot: String, val locale: String, val device: String, val inputs: String)

/** Bounded thumbnails retain encoded pixels rather than full store-size canvas backing stores. */
data class Thumbnail(val dataUrl: String, val report: RenderReport)

/** Per-screen lifetime prevents thumbnails crossing project or account boundaries. */
class ThumbnailCache(private val limit: Int = 160) {
    private val entries = linkedMapOf<ThumbnailKey, Thumbnail>()
    init { require(limit > 0) }
    /** Refreshes recency so common language switches stay instant. */
    fun get(key: ThumbnailKey): Thumbnail? = entries.remove(key)?.also { entries[key] = it }
    /** Evicts oldest pixels before the cache can grow without bound. */
    fun put(key: ThumbnailKey, value: Thumbnail) {
        entries.remove(key)
        entries[key] = value
        while (entries.size > limit) entries.remove(entries.keys.first())
    }
    /** Frees private preview pixels when the project screen leaves. */
    fun clear() { entries.clear() }
}

/** Exactly one draw starts per animation frame, including bursts of locale changes. */
class PreviewQueue {
    private val pending = linkedMapOf<String, () -> Unit>()
    private var frame: Int? = null
    /** A new version replaces work for the same cell before it reaches the frame. */
    fun enqueue(id: String, priority: Boolean = false, render: () -> Unit) {
        pending.remove(id)
        if (priority) {
            val tail = pending.toMap()
            pending.clear()
            pending[id] = render
            pending.putAll(tail)
        } else pending[id] = render
        schedule()
    }
    /** Disposal retires a queued cell without disturbing another mounted thumbnail. */
    fun cancel(id: String) { pending.remove(id) }
    /** Cancels browser work when its project is no longer visible. */
    fun close() {
        frame?.let(window::cancelAnimationFrame)
        frame = null
        pending.clear()
    }
    private fun schedule() {
        if (frame != null || pending.isEmpty()) return
        frame = window.requestAnimationFrame {
            frame = null
            val id = pending.keys.firstOrNull()
            val next = id?.let(pending::remove)
            try { next?.invoke() } finally { schedule() }
        }
    }
}

/** A screen shares one scheduler, pixel cache and image owner across every template card. */
class PreviewResources(looks: DesignLooksPort) {
    /** Bounds encoded thumbnail storage across the whole gallery. */
    val cache = ThumbnailCache()
    /** Enforces the one-draw-per-frame budget across cards, including hover reprioritization. */
    val queue = PreviewQueue()
    /** Shares capture fetches and closes decoded images with this screen. */
    val images = PreviewBitmapStore()
    /** Keeps fit caching scoped to the screen rather than individual cards. */
    val renderer = PreviewRenderer(looks)
    /** Retires pending draws and private pixels together on route or project changes. */
    fun close() { queue.close(); images.close(); cache.clear() }
}
